import { useEffect, useRef } from "react";
import { DEFAULT_AURORA, useConfigStore } from "../../Stores/Config";
import { createPulseOrb, SILENT_LEVELS } from "./kaiVoiceOrb";
import type { VoiceLevels } from "./kaiVoiceOrb";

interface KaiVoiceParticlesProps {
  /** 0..1 per side, read every frame: Kai swells and ripples the orb, the customer draws it in. */
  getLevels: () => VoiceLevels;
  /** Kai is working on an answer: the sphere gathers in a breath around a mint core. */
  thinking?: boolean;
  /** Harness mode: seeded layout + a single static frame, so VR runs are deterministic. */
  frozen?: boolean;
}

// Tiny deterministic PRNG for frozen (VR) rendering.
const createSeededRandom = (seed: number) => () => {
  seed = (seed * 1664525 + 1013904223) % 4294967296;
  return seed / 4294967296;
};

/**
 * The Kai voice presence: the Pulse Orb (kaiVoiceOrb.ts) on a canvas covering
 * the call screen, tinted from the project's aurora palette.
 */
export const KaiVoiceParticles = ({ getLevels, thinking = false, frozen }: KaiVoiceParticlesProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const getLevelsRef = useRef(getLevels);
  getLevelsRef.current = getLevels;
  const thinkingRef = useRef(thinking);
  thinkingRef.current = thinking;

  const auroraColors = useConfigStore((state) => state.getAuroraColors?.() ?? undefined);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    const reducedMotion =
      frozen || (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false);
    const drawOrb = createPulseOrb({
      palette: auroraColors && auroraColors.length > 0 ? auroraColors : DEFAULT_AURORA,
      random: frozen ? createSeededRandom(1337) : Math.random,
      reducedMotion,
    });

    let rafId: number | null = null;
    const draw = (time: number) => {
      const dpr = window.devicePixelRatio || 1;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
        canvas.width = width * dpr;
        canvas.height = height * dpr;
      }
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      let levels = SILENT_LEVELS;
      try {
        levels = getLevelsRef.current() ?? SILENT_LEVELS;
      } catch (_) {
        // Metering is cosmetic: a broken analyser leaves the orb calm.
      }
      drawOrb(time, {
        context,
        width,
        height,
        centerX: width / 2,
        centerY: height / 2,
        size: Math.min(width, height),
        levels,
        thinking: thinkingRef.current,
      });
      if (!reducedMotion) {
        rafId = window.requestAnimationFrame(draw);
      }
    };
    rafId = window.requestAnimationFrame(draw);

    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [auroraColors, frozen]);

  return <canvas aria-hidden ref={canvasRef} className="kai-voice-call--particles" />;
};
